import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { BrandLogo } from "../components/Layout";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Kata sandi minimal 6 karakter");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Akun berhasil dibuat! Sekarang atur PIN transaksimu.");
      navigate("/atur-pin");
    } catch (err) {
      toast.error(errMsg(err, "Gagal mendaftar. Coba lagi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <BrandLogo />
          <p className="text-xs text-muted-foreground mt-1">Dompet Digital & Transfer</p>
        </div>
        <Card className="rounded-2xl shadow-[0_10px_30px_rgba(11,18,32,0.08)]">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading text-2xl font-semibold mb-1">Daftar</h2>
            <p className="text-sm text-muted-foreground mb-6">Buat akun dan mulai transfer gratis.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Nama Lengkap</Label>
                <Input id="name" data-testid="register-name-input" placeholder="Nama sesuai KTP" value={form.name} onChange={set("name")} className="h-11" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="register-email-input" placeholder="nama@email.com" value={form.email} onChange={set("email")} className="h-11" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Nomor HP</Label>
                <Input id="phone" data-testid="register-phone-input" placeholder="08xxxxxxxxxx" value={form.phone} onChange={set("phone")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Kata Sandi</Label>
                <div className="relative">
                  <Input id="password" type={showPass ? "text" : "password"} data-testid="register-password-input" placeholder="Minimal 6 karakter" value={form.password} onChange={set("password")} className="h-11 pr-10" required />
                  <button type="button" aria-label="Tampilkan kata sandi" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="register-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Daftar"}
              </Button>
            </form>
            <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
              Setelah daftar, kamu akan diminta mengatur PIN 6 digit untuk keamanan transaksi.
            </div>
            <p className="text-sm text-center text-muted-foreground mt-6">
              Sudah punya akun?{" "}
              <Link to="/masuk" className="text-primary font-medium hover:underline" data-testid="register-login-link">Masuk</Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
